import React from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { ReactComponent as SwitchDeviceIcon } from '../../assets/images/switch-device.svg';
import { ReactComponent as NoDevicesIcon } from '../../assets/images/Shape.svg';

export interface SwitchDeviceMenuItem {
    deviceId: string;
    deviceName: string;
    lastActiveAt: string;
    deviceType: string;
}

export interface SwitchDeviceMenuProps {
    devices: SwitchDeviceMenuItem[];
    onSelect: (device: SwitchDeviceMenuItem) => void;
    /** Device whose switch request is in flight; its icon becomes a spinner. */
    switchingDeviceId?: string;
}

const Menu = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: min(280px, calc(100vw - 32px));
    max-height: min(600px, calc(100vh - 140px));
    overflow: hidden;
    padding: 8px 0;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(17, 17, 17, 0.6);
    backdrop-filter: blur(8px);
    font-family: 'Source Sans Pro', sans-serif;
`;

const Heading = styled.h2`
    display: flex;
    align-items: center;
    min-height: 48px;
    margin: 0;
    padding: 0 20px;
    font-size: 19px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.6);
`;

const DeviceList = styled.ul`
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: 0;

    &::-webkit-scrollbar {
        width: 12px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 999px;
        border: 2px solid transparent;
        background-clip: content-box;
    }
`;

const DeviceRow = styled.button`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    height: 64px;
    padding: 0 20px;
    text-align: left;

    &:focus:not(:focus-visible) {
        outline: none;
    }

    &:not(:disabled):hover {
        background: rgba(255, 255, 255, 0.08);
    }
    &:not(:disabled):active {
        background: rgba(255, 255, 255, 0.12);
    }

    /* Dimmed while another device's switch request is in flight. */
    &:disabled {
        opacity: 0.3;
        cursor: default;
    }
`;

const RowIcon = styled(SwitchDeviceIcon)`
    flex: 0 0 28px;
    height: 28px;
`;

/* The icon is a fixed white fill, so the spinner that replaces it sets its own. */
const RowSpinner = styled(CircularProgress)`
    flex: 0 0 28px;
    color: #ffffff;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;

const Name = styled.span`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #ffffff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LastActive = styled.span`
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: rgba(255, 255, 255, 0.6);
`;

const Message = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 20px;
    text-align: center;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
`;

const MessageHint = styled.span`
    font-size: 12px;
    font-weight: 600;
    line-height: 150%;
    color: rgba(255, 255, 255, 0.6);
`;

const SwitchDeviceMenu: React.FC<SwitchDeviceMenuProps> = ({
    devices,
    onSelect,
    switchingDeviceId = '',
}) => {
    const renderBody = (): React.ReactNode => {
        if (devices.length === 0) {
            return (
                <Message>
                    <NoDevicesIcon aria-hidden="true" />
                    <span>No other devices</span>
                    <MessageHint>
                        Sign in to Convo Now on another
                        <br />
                        device to move your call there.
                    </MessageHint>
                </Message>
            );
        }

        return (
            <DeviceList aria-labelledby="switch-device-heading">
                {devices.map((device, index) => (
                    <li key={device.deviceId}>
                        <DeviceRow
                            autoFocus={index === 0}
                            disabled={!!switchingDeviceId && device.deviceId !== switchingDeviceId}
                            onClick={() => onSelect(device)}
                        >
                            {device.deviceId === switchingDeviceId ? (
                                <RowSpinner size={28} aria-hidden="true" />
                            ) : (
                                <RowIcon aria-hidden="true" />
                            )}
                            <Content>
                                <Name title={device.deviceName}>{device.deviceName}</Name>
                                <LastActive>{device.lastActiveAt}</LastActive>
                            </Content>
                        </DeviceRow>
                    </li>
                ))}
            </DeviceList>
        );
    };

    return (
        <Menu>
            {devices.length > 0 && (
                <Heading id="switch-device-heading" aria-label="Select a device to switch to">
                    Select a device to switch to
                </Heading>
            )}
            {renderBody()}
        </Menu>
    );
};

export default SwitchDeviceMenu;
